import { z } from "zod";

// Pairwise LLM judging of two agent variants over one URL. Pinned onto every
// eval.judge.requested message by the backend publisher and read off the
// message by workers/eval-judge.
export const EvalJudgeConfigSchema = z
  .object({
    // The judge must hold a long, structured briefing (two full variant
    // outputs) in context and answer in strict JSON; kimi-k2 is the model
    // already proven at that shape on the canonicalize path.
    judgeModel: z.string().default("moonshotai/kimi-k2-0905"),
    // Corrective retries after output that fails the pass schema or the
    // per-metric coverage contract, before the comparison fails loudly.
    maxRetries: z.number().int().min(0).default(2),
    maxOutputTokens: z.number().int().positive().default(8_192),
    // Concurrent comparisons per judge process (channel QoS). Two passes plus
    // a briefing fetch per message, so this is deliberately small.
    prefetch: z.number().int().positive().default(2),
    // Wall clock for one judge pass; past it the pass aborts and the message
    // dead-letters rather than holding the delivery open forever. Doubles as
    // the claim age past which the backend's batch sweep considers the request
    // lost (worker died, queue purged by a deploy) and re-publishes.
    requestTimeoutMs: z
      .number()
      .int()
      .positive()
      .default(10 * 60_000),
    // Total publishes (first + sweep re-publishes) before the comparison fails
    // instead of being retried forever.
    maxRequests: z.number().int().positive().default(3),
  })
  .strict();
export type EvalJudgeConfig = z.infer<typeof EvalJudgeConfigSchema>;

/**
 * The backend's own eval surfaces: the sweep that advances batches, and the
 * bounds on the admin lists that read them. Nothing here is pinned onto a
 * message — the backend consumes all of it — so it sits with the other
 * backend-consumed namespaces in spirit, and here in practice because it is
 * eval vocabulary.
 */
export const EvalBatchConfigSchema = z
  .object({
    // Comparisons examined per tick. The sweep does per-comparison DB work and
    // may construct a run or publish a judge request for each, so a batch of
    // hundreds is advanced across several ticks rather than in one burst.
    sweepComparisonLimit: z.number().int().positive().default(50),
    // Settled batches whose completion is reconciled per tick.
    sweepBatchLimit: z.number().int().positive().default(20),
    // Consecutive failures to advance one comparison before it is failed
    // outright. A transient error (broker down, a slow donor) must be retried,
    // but a persistent one — a poisoned row the sweep throws on every tick —
    // would otherwise be retried forever and hold its batch open for good.
    maxAdvanceFailures: z.number().int().positive().default(5),
    // Batches on one page of the admin batch list, newest first.
    batchListLimit: z.number().int().positive().default(50),
    // Suites on one page of the admin suite list, newest first.
    suiteListLimit: z.number().int().positive().default(100),
  })
  .strict();
export type EvalBatchConfig = z.infer<typeof EvalBatchConfigSchema>;
